import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase, STORAGE_KEY } from '../lib/supabase'
import { AuthContext } from './authContext'
import useInactivityTimeout from '../hooks/useInactivityTimeout'
import InactivityWarningModal from '../components/InactivityWarningModal'
import i18n from '../lib/i18n'

async function fetchProfileDirect(userId, accessToken) {
  if (!userId || !accessToken) return null
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=*`
    const res = await fetch(url, {
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.pgrst.object+json',
      },
    })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

// Try to recover session from localStorage when GoTrueClient fails
function getStoredSession() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch { /* ignore */ }
  return null
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const initialized = useRef(false)

  const role = user?.app_metadata?.role ?? null
  const status = profile?.status ?? null

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return null
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        const data = await fetchProfileDirect(user.id, session.access_token)
        setProfile(data)
        return data
      }
    } catch {
      // ignore
    }
    return null
  }, [user?.id])

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        // If onAuthStateChange fires with no session on initial load,
        // try to recover from localStorage (GoTrueClient may have aborted
        // the session recovery due to its internal AbortController bug)
        if (!session && !initialized.current) {
          const stored = getStoredSession()
          if (stored?.user && stored?.access_token) {
            session = stored
          }
        }

        const currentUser = session?.user ?? null
        setUser(currentUser)

        if (currentUser && session?.access_token) {
          let data = await fetchProfileDirect(currentUser.id, session.access_token)

          // Sync profiles.email after email change verification completes.
          // Only sync when pending_email matches the new auth email — this avoids
          // reverting admin-initiated email changes from stale JWT sessions.
          if (data?.pending_email && currentUser.email === data.pending_email) {
            try {
              await supabase.from('profiles')
                .update({ email: currentUser.email, pending_email: null })
                .eq('id', currentUser.id)
              data = await fetchProfileDirect(currentUser.id, session.access_token)
            } catch { /* ignore sync errors */ }
          }

          setProfile(data)
          if (data?.preferred_language) {
            i18n.changeLanguage(data.preferred_language)
          }
        } else {
          setProfile(null)
        }

        // Always mark as loaded after first auth event
        if (!initialized.current) {
          initialized.current = true
          setLoading(false)
        }
      }
    )

    // Fallback: if onAuthStateChange never fires, unblock after timeout
    const timer = setTimeout(() => {
      if (!initialized.current) {
        const stored = getStoredSession()
        if (stored?.user && stored?.access_token) {
          setUser(stored.user)
          fetchProfileDirect(stored.user.id, stored.access_token).then(data => {
            setProfile(data)
            if (data?.preferred_language) {
              i18n.changeLanguage(data.preferred_language)
            }
          })
        }
        initialized.current = true
        setLoading(false)
      }
    }, 1000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timer)
    }
  }, [])

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signOut = async () => {
    // Clear localStorage session first to prevent fallback restoration
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    try {
      await supabase.auth.signOut()
    } catch {
      // signOut may throw AbortError, but we've already cleared localStorage
    }
    setUser(null)
    setProfile(null)
  }

  const { showWarning, timeRemaining, resetTimer } = useInactivityTimeout({
    enabled: !!user,
    onTimeout: signOut,
    warningMs: 55 * 60 * 1000,
    timeoutMs: 60 * 60 * 1000,
  })

  return (
    <AuthContext.Provider value={{ user, role, status, profile, loading, signIn, signOut, refreshProfile }}>
      <InactivityWarningModal
        open={showWarning}
        timeRemaining={timeRemaining}
        onStaySignedIn={resetTimer}
        onSignOut={signOut}
      />
      {children}
    </AuthContext.Provider>
  )
}
